import * as React from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
	easing,
	Grid,
	List,
	ListItem,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Menu,
	MenuItem,
	Skeleton,
	Slide,
	useMediaQuery,
	useTheme,
} from '@mui/material';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarLinesPen, faChevronsDown, faChevronsUp } from '@fortawesome/pro-solid-svg-icons';
import {
	faPhoneFlip,
	faCircleQuestion,
	faEnvelopeOpenText,
	faCheckSquare,
	faRocket,
	faLink,
} from '@fortawesome/free-solid-svg-icons';

import { EnumUtils, NumberUtils } from '../../utils';
import { CerebroIcon, CerebroLink, CerebroText, CerebroTextButton } from '..';
import {
	useDataRoomConfirmationQuery,
	useDataRoomQuery,
	useDataRoomSummaryQuery,
	useDealQuery,
	useIntegrationConfigQuery,
} from '../../hooks';
import { Urls, useAuth } from '../../context';
import { CerebroPhoneNumber, CerebroPhoneNumberHref, LoanRequestUrl } from '../../constants';
import { MilestoneStatusItem } from './MilestoneStatusItem';
import { ActiveDataRoomUser, CurrentUser, DataRoomConfirmation } from '../../models';
import { MilestoneReport } from '../../models/MilestoneReport';
import { EmployeeAppRootUrl, LoanApplicationUrl } from '../../utils/platform';
import { CerebroPopup } from '../CerebroPopup';
import { useDataRoomLenderStatusBarQuery } from '../../hooks/Queries/useDataRoomLenderStatusBarQuery';
import { getEditUrl } from '../../../pages';
import { DirectConnectMilestoneStatusItem } from './DirectConnectMilestoneStatusItem';
import {
	DirectConnectMilestoneMarkCompleteDialog,
	DirectConnectMilestoneMarkRequest,
} from './DirectConnectMilestoneMarkCompleteDialog';
import { ContactBorrowerForm } from './ContactBorrowerForm';

export const StatusBar: React.FC<StatusBarProps> = props => {
	const theme = useTheme();
	const { currentUser } = useAuth();

	const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));

	if (!isDesktop) {
		if (currentUser.isBankUser) {
			return (
				<MobileLenderStatusBar
					isDirectIntroDeal={props.isDirectIntroDeal}
					lenderCallRequest={props.lenderCallRequest}
					lenderOptOut={props.lenderOptOut}
				/>
			);
		}
		return <MobileStatusBar />;
	}

	if (props.isDirectIntroDeal && currentUser.isBankUser) {
		return <LenderDirectConnectStatusBar lenderOptOut={props.lenderOptOut} />;
	}

	return currentUser.isBankUser ? (
		<DesktopLenderStatusBar lenderCallRequest={props.lenderCallRequest} lenderOptOut={props.lenderOptOut} />
	) : (
		<DesktopStatusBar currentUser={currentUser} launchDeal={props.launchDeal} />
	);
};

const MobileHeaderHeight = 64;

export const MobileStatusBarListItemHeight = 46;
const NumberOfListItems = 5;

const MobileStatusBarOpenPosition = MobileHeaderHeight;
const MobileStatusBarClosedPosition = MobileHeaderHeight - MobileStatusBarListItemHeight * NumberOfListItems;
const MobileStatusBarLoadingPosition = MobileStatusBarClosedPosition - MobileStatusBarListItemHeight;

const MobileStatusBar: React.FC = () => {
	const theme = useTheme();
	const { isSuccess } = useStatusData();

	const location = useLocation();

	const [open, setOpen] = React.useState(false);

	let top = MobileStatusBarLoadingPosition;

	if (isSuccess) top = open ? MobileStatusBarOpenPosition : MobileStatusBarClosedPosition;

	React.useEffect(() => {
		setOpen(false);
	}, [location, setOpen]);

	return (
		<List
			dense
			disablePadding
			sx={{
				backgroundColor: theme.palette.gray.background,
				borderBottom: `2px solid ${theme.palette.primary.main}`,
				boxShadow: theme.yellowShadows[4],
				boxSizing: 'border-box',

				top,
				position: 'absolute',
				width: '100%',

				transition: theme.transitions.create(['all']),

				zIndex: 100,
			}}
		>
			<ListItem disableGutters></ListItem>

			<ListItem disableGutters></ListItem>

			<ListItem disableGutters>
				<ListItemButton component="a" href={BuildEmailUrl()}>
					<ListItemText disableTypography>
						<CerebroText align="center" component="span" weight="medium" sx={{ display: 'inline-block' }}>
							Email Us
						</CerebroText>
					</ListItemText>
				</ListItemButton>
			</ListItem>

			<ListItem disableGutters>
				<ListItemButton component="a" href={LoanRequestUrl}>
					<ListItemText disableTypography>
						<CerebroText align="center" component="span" weight="medium" sx={{ display: 'inline-block' }}>
							Schedule a Call
						</CerebroText>
					</ListItemText>
				</ListItemButton>
			</ListItem>

			<ListItem disableGutters divider>
				<ListItemButton component="a" href={CerebroPhoneNumberHref}>
					<ListItemText disableTypography>
						<CerebroText align="center" component="span" weight="medium" sx={{ display: 'inline-block' }}>
							Call {CerebroPhoneNumber}
						</CerebroText>
					</ListItemText>
				</ListItemButton>
			</ListItem>

			<ListItem disableGutters>
				<ListItemButton onClick={() => setOpen(currentValue => !currentValue)}>
					<ListItemText>
						<CerebroText component="span" weight="medium">
							{open ? 'Close' : 'Get Help'}
						</CerebroText>
					</ListItemText>
					<ListItemIcon sx={{ justifyContent: 'flex-end' }}>
						<FontAwesomeIcon icon={open ? faChevronsUp : faChevronsDown} />
					</ListItemIcon>
				</ListItemButton>
			</ListItem>
		</List>
	);
};

const MobileLenderStatusBar: React.FC<
	Pick<StatusBarProps, 'isDirectIntroDeal' | 'lenderCallRequest' | 'lenderOptOut'>
> = ({ isDirectIntroDeal, lenderCallRequest, lenderOptOut }) => {
	const theme = useTheme();
	const { guid } = useParams<'guid'>();
	const { isSuccess, isFetching, report, activeUser } = useLenderStatusData();
	const [contactBorrowerOpen, setContactBorrowerOpen] = React.useState(false);

	const readyForCall = report != null && report.documentsDownloaded == 'Yes';

	let top = MobileStatusBarLoadingPosition;

	if (isSuccess) top = MobileStatusBarOpenPosition;

	return (
		<Slide mountOnEnter in={isSuccess} easing={easing.easeOut} timeout={500}>
			<div>
				<Grid
					container
					flexWrap="nowrap"
					alignItems="center"
					justifyContent="space-between"
					sx={{
						borderBottom: `2px solid ${theme.palette.primary.main}`,
						boxShadow: theme.yellowShadows[4],
						paddingX: 2,
						paddingY: 1,
					}}
				>
					<Grid item sx={{ display: 'flex', alignItems: 'center' }}>
						{!isDirectIntroDeal && readyForCall && (
							<CerebroTextButton
								onClick={() => lenderCallRequest(activeUser)}
								title="Request of a call with the borrower"
							>
								Schedule Call
							</CerebroTextButton>
						)}
						{isDirectIntroDeal && (
							<CerebroTextButton onClick={() => setContactBorrowerOpen(true)}>
								Contact Borrower
							</CerebroTextButton>
						)}
						<CerebroTextButton onClick={() => lenderOptOut(activeUser)}>Opt-Out</CerebroTextButton>
					</Grid>
				</Grid>

				{isDirectIntroDeal && (
					<ContactBorrowerForm
						dataRoomGuid={guid}
						bankContactGuid={activeUser?.bankContactGuid}
						open={contactBorrowerOpen}
						close={() => setContactBorrowerOpen(false)}
					/>
				)}
			</div>
		</Slide>
	);
};

const DesktopStatusBar: React.FC<Pick<StatusBarProps, 'currentUser' | 'launchDeal'>> = ({ currentUser, launchDeal }) => {
	const theme = useTheme();
	const { guid } = useParams<'guid'>();
	const navigate = useNavigate();

	const {
		isSuccess,
		isFetching,
		summaryData,
		requestedAmount,
		confirmation,
		dealGuid,
		hubspotId,
		companyGuid,
		hubspotPortalId,
		isRawLead,
	} = useStatusData();

	const editUrl = getEditUrl(isRawLead, dealGuid ?? '');

	const [helpMenuAnchorElement, setHelpMenuAnchorElement] = React.useState<null | HTMLElement>(null);

	const helpMenuOpen = Boolean(helpMenuAnchorElement);
	const onOpenHelpMenu = (event: React.MouseEvent<HTMLButtonElement>) =>
		setHelpMenuAnchorElement(event.currentTarget);
	const onCloseHelpMenu = () => setHelpMenuAnchorElement(null);
	const onConfirmTeasers = () => navigate(`${guid}/${Urls.Teasers.Root}`);
	const onLaunchDeal = () => launchDeal(confirmation);

	return (
		<Slide mountOnEnter in={isSuccess} easing={easing.easeOut} timeout={500}>
			<div>
				<Grid
					container
					flexWrap="nowrap"
					alignItems="center"
					justifyContent="space-between"
					sx={{
						borderBottom: `2px solid ${theme.palette.primary.main}`,
						boxShadow: theme.yellowShadows[4],
						paddingX: 2,
						paddingY: 1,
					}}
				>
					<Grid item sx={{ display: 'flex', alignItems: 'center' }}>
						<Grid item sx={{ marginRight: 3 }}>
							<CerebroText variant="small" gutterBottom align="center" weight="medium">
								Amount Requested
							</CerebroText>
							{isFetching ? (
								<Skeleton
									animation="wave"
									sx={theme => ({ backgroundColor: theme.palette.primary.light })}
								/>
							) : (
								<CerebroLink
									component={Link}
									sx={{ display: 'block' }}
									to={`${guid}/${Urls.Summary.Root}`}
									variant="small"
									align="center"
								>
									{requestedAmount === 0 ? '--' : NumberUtils.FormatCurrency(requestedAmount)}
								</CerebroLink>
							)}
						</Grid>
						<Grid item>
							<CerebroText variant="small" gutterBottom align="center" weight="medium">
								Active Lenders
							</CerebroText>
							{isFetching ? (
								<Skeleton
									animation="wave"
									sx={theme => ({ backgroundColor: theme.palette.primary.light })}
								/>
							) : (
								<CerebroLink
									component={Link}
									sx={{ display: 'block' }}
									to={`${guid}/${Urls.Milestones.Root}`}
									variant="small"
									align="center"
								>
									{summaryData?.lendersActive}
								</CerebroLink>
							)}
						</Grid>
						<Grid item sx={{ paddingLeft: 3 }}>
							<CerebroText variant="small" gutterBottom align="center" weight="medium">
								Pending Diligence
							</CerebroText>
							{isFetching ? (
								<Skeleton
									animation="wave"
									sx={theme => ({ backgroundColor: theme.palette.primary.light })}
								/>
							) : (
								<CerebroLink
									component={Link}
									sx={{ display: 'block' }}
									to={`${guid}/${Urls.DiligenceRequests.Root}`}
									variant="small"
									align="center"
								>
									{summaryData?.diligenceRequestsOpen}
								</CerebroLink>
							)}
						</Grid>
						{currentUser.isEmployeeUser && !isRawLead && (
							<Grid item sx={{ paddingLeft: 3 }}>
								<CerebroText variant="small" gutterBottom align="center" weight="medium">
									Deal Status
								</CerebroText>
								<CerebroLink
									sx={{ display: 'block' }}
									href={`${LoanApplicationUrl}/${confirmation?.dealGuid}/UpdateDealStatus`}
									variant="small"
									align="center"
								>
									{EnumUtils.GetDealStatusDescription(confirmation?.dealStatus)}
								</CerebroLink>
							</Grid>
						)}
						{(currentUser.isEmployeeUser || currentUser.isCompanyUser) && (
							<Grid item sx={{ paddingLeft: 3 }}>
								<CerebroText variant="small" gutterBottom align="center" weight="medium">
									Data Room Status
								</CerebroText>
								<CerebroText variant="small" align="center" sx={{ display: 'block' }}>
									{EnumUtils.GetDataRoomStatusDescription(confirmation?.roomStatus)}
								</CerebroText>
							</Grid>
						)}
					</Grid>

					<Grid item sx={{ display: 'flex', alignItems: 'center' }}>
						{currentUser.isEmployeeUser &&
							confirmation?.dealCanBeLaunched &&
							!confirmation.teaserListConfirmed &&
							confirmation.hasUnsentTeasers && (
								<CerebroTextButton id="confirm-teasers" onClick={onConfirmTeasers}>
									<CerebroIcon sx={{ color: theme.palette.primary.main, marginRight: 1 }}>
										<FontAwesomeIcon icon={faCheckSquare} />
									</CerebroIcon>
									Confirm Teaser List
								</CerebroTextButton>
							)}
						{currentUser.isEmployeeUser &&
							confirmation?.dealCanBeLaunched &&
							confirmation.teaserListConfirmed && (
								<CerebroTextButton id="launch-deal" onClick={onLaunchDeal}>
									<CerebroIcon sx={{ color: theme.palette.primary.main, marginRight: 1 }}>
										<FontAwesomeIcon icon={faRocket} />
									</CerebroIcon>
									Launch Deal
								</CerebroTextButton>
							)}

						{!currentUser.isEmployeeUser ? (
							<>
								<CerebroLink
									href={CerebroPhoneNumberHref}
									variant="small"
									align="center"
									sx={{ marginRight: 2 }}
								>
									<CerebroIcon sx={{ color: theme.palette.primary.main, marginRight: 1 }}>
										<FontAwesomeIcon icon={faPhoneFlip} />
									</CerebroIcon>
									{CerebroPhoneNumber}
								</CerebroLink>

								<CerebroTextButton
									id="basic-button"
									aria-controls={helpMenuOpen ? 'basic-menu' : undefined}
									aria-haspopup="true"
									aria-expanded={helpMenuOpen ? 'true' : undefined}
									onClick={onOpenHelpMenu}
								>
									Need Help
									<CerebroIcon sx={{ color: theme.palette.primary.main, marginLeft: 1 }}>
										<FontAwesomeIcon icon={faCircleQuestion} size="lg" />
									</CerebroIcon>
								</CerebroTextButton>
								<Menu
									id="basic-menu"
									anchorEl={helpMenuAnchorElement}
									open={helpMenuOpen}
									onClose={onCloseHelpMenu}
									MenuListProps={{
										'aria-labelledby': 'basic-button',
									}}
								>
									<MenuItem onClick={onCloseHelpMenu}>
										<ListItemIcon sx={{ color: theme.palette.primary.main }}>
											<FontAwesomeIcon icon={faPhoneFlip} />
										</ListItemIcon>
										<ListItemText>Call {CerebroPhoneNumber}</ListItemText>
									</MenuItem>

									<MenuItem
										onClick={onCloseHelpMenu}
										component="a"
										href={LoanRequestUrl}
										target="_blank"
									>
										<ListItemIcon sx={{ color: theme.palette.primary.main }}>
											<FontAwesomeIcon icon={faCalendarLinesPen} />
										</ListItemIcon>
										<ListItemText>Schedule a Call</ListItemText>
									</MenuItem>

									<MenuItem onClick={onCloseHelpMenu} component="a" href={BuildEmailUrl()}>
										<ListItemIcon sx={{ color: theme.palette.primary.main }}>
											<FontAwesomeIcon icon={faEnvelopeOpenText} />
										</ListItemIcon>
										<ListItemText>Email Us</ListItemText>
									</MenuItem>
								</Menu>
							</>
						) : (
							<>
								<CerebroTextButton
									id="basic-button"
									aria-controls={helpMenuOpen ? 'basic-menu' : undefined}
									aria-haspopup="true"
									aria-expanded={helpMenuOpen ? 'true' : undefined}
									onClick={onOpenHelpMenu}
								>
									Quick Links
									<CerebroIcon sx={{ color: theme.palette.primary.main, marginLeft: 1 }}>
										<FontAwesomeIcon icon={faLink} size="lg" />
									</CerebroIcon>
								</CerebroTextButton>
								<Menu
									id="basic-menu"
									anchorEl={helpMenuAnchorElement}
									open={helpMenuOpen}
									onClose={onCloseHelpMenu}
									MenuListProps={{
										'aria-labelledby': 'basic-button',
									}}
								>
									<MenuItem
										onClick={onCloseHelpMenu}
										component="a"
										href={`${editUrl}`}
										target="_blank"
									>
										<ListItemText>Loan Application</ListItemText>
									</MenuItem>
									{!isRawLead && (
										<MenuItem
											onClick={onCloseHelpMenu}
											component="a"
											href={`${EmployeeAppRootUrl}/Companies/${companyGuid}/Overview`}
											target="_blank"
										>
											<ListItemText>Company Summary</ListItemText>
										</MenuItem>
									)}
									{!isRawLead && (
										<MenuItem
											onClick={onCloseHelpMenu}
											component={Link}
											to={`${guid}/RelatedDeals`}
										>
											<ListItemText>Similar Deals</ListItemText>
										</MenuItem>
									)}
									<MenuItem
										onClick={onCloseHelpMenu}
										component="a"
										href={`https://app.hubspot.com/contacts/${hubspotPortalId}/deal/${hubspotId}/`}
										target="_blank"
									>
										<ListItemText>HubSpot</ListItemText>
									</MenuItem>
								</Menu>
							</>
						)}
					</Grid>
				</Grid>
			</div>
		</Slide>
	);
};

const DesktopLenderStatusBar: React.FC<Pick<StatusBarProps, 'lenderCallRequest' | 'lenderOptOut'>> = ({
	lenderCallRequest,
	lenderOptOut,
}) => {
	const theme = useTheme();
	const { isSuccess, isFetching, report, activeUser } = useLenderStatusData();
	const readyForCall = report != null && report.documentsDownloaded == 'Yes';

	const [tutorialOpen, setTutorialOpen] = React.useState(false);
	const onCloseDrawersAndDialogs = () => {
		setTutorialOpen(false);
	};

	const callInProgress =
		report != null &&
		(report.dataRoomCallGuid ?? '') != '' &&
		report.callStatus != 'Completed' &&
		!report.hasCompletedCalls &&
		report.callStatus != 'Cancelled';

	return (
		<Slide mountOnEnter in={isSuccess} easing={easing.easeOut} timeout={500}>
			<div>
				<Grid
					container
					flexWrap="nowrap"
					alignItems="center"
					justifyContent="space-between"
					sx={{
						borderBottom: `2px solid ${theme.palette.primary.main}`,
						boxShadow: theme.yellowShadows[4],
						paddingX: 2,
						paddingY: 1,
					}}
				>
					<Grid item sx={{ display: 'flex', alignItems: 'center' }}>
						<Grid item sx={{ marginRight: 3, textAlign: 'center' }}>
							<MilestoneStatusItem
								size={2}
								isFetching={isFetching}
								display="Teaser"
								type="Teaser"
								inProgress={false}
								response={report?.teaser}
							/>
						</Grid>
						<Grid item sx={{ marginRight: 3, textAlign: 'center' }}>
							<MilestoneStatusItem
								size={2}
								isFetching={isFetching}
								display="Access Data Room"
								type="AccessDeal"
								inProgress={false}
								response={report?.accessDeal}
							/>
						</Grid>
						<Grid item sx={{ marginRight: 3, textAlign: 'center' }}>
							<MilestoneStatusItem
								size={2}
								isFetching={isFetching}
								display="Review Documents"
								type="DocumentsDownloaded"
								inProgress={false}
								response={report?.documentsDownloaded}
							/>
						</Grid>
						<Grid item sx={{ marginRight: 3, textAlign: 'center' }}>
							<MilestoneStatusItem
								size={2}
								isFetching={isFetching}
								display="Call w/ Borrower"
								type="ReadyForCall"
								inProgress={callInProgress}
								response={report?.readyForCall}
							/>
						</Grid>
						<Grid item sx={{ marginRight: 3, textAlign: 'center' }}>
							<MilestoneStatusItem
								size={2}
								isFetching={isFetching}
								display="Term Sheet"
								type="TermSheetSubmitted"
								inProgress={false}
								response={report?.termSheetSubmitted}
							/>
						</Grid>
						<Grid item sx={{ marginRight: 3, textAlign: 'center' }}>
							<MilestoneStatusItem
								size={2}
								isFetching={isFetching}
								display="Underwriting"
								type="Underwriting"
								inProgress={false}
								response={report?.underwriting}
							/>
						</Grid>
						<Grid item sx={{ marginRight: 3, textAlign: 'center' }}>
							<MilestoneStatusItem
								size={2}
								isFetching={isFetching}
								display="Funded"
								type="Funded"
								inProgress={false}
								response={report?.funded}
							/>
						</Grid>
					</Grid>

					<Grid item sx={{ display: 'flex', alignItems: 'center' }}>
						{readyForCall && (
							<CerebroTextButton
								onClick={() => lenderCallRequest(activeUser)}
								title="Request of a call with the borrower"
							>
								Schedule Call
							</CerebroTextButton>
						)}
						<CerebroTextButton onClick={() => lenderOptOut(activeUser)}>Opt-Out</CerebroTextButton>
						<CerebroTextButton onClick={() => setTutorialOpen(true)}>Tutorial</CerebroTextButton>

						<CerebroPopup open={tutorialOpen} close={onCloseDrawersAndDialogs} />
					</Grid>
				</Grid>
			</div>
		</Slide>
	);
};

const LenderDirectConnectStatusBar: React.FC<Pick<StatusBarProps, 'lenderOptOut'>> = ({ lenderOptOut }) => {
	const theme = useTheme();
	const { guid } = useParams<'guid'>();
	const { isSuccess, isFetching, directConnectReport, activeUser } = useLenderStatusData();

	const [markingMilestone, setMarkingMilestone] = React.useState<DirectConnectMilestoneMarkRequest | null>(null);
	const [contactBorrowerOpen, setContactBorrowerOpen] = React.useState(false);

	return (
		<Slide mountOnEnter in={isSuccess} easing={easing.easeOut} timeout={500}>
			<div>
				<Grid
					container
					flexWrap="nowrap"
					alignItems="center"
					justifyContent="space-between"
					sx={{
						borderBottom: `2px solid ${theme.palette.primary.main}`,
						boxShadow: theme.yellowShadows[4],
						paddingX: 2,
						paddingY: 1,
					}}
				>
					<Grid item sx={{ display: 'flex', alignItems: 'center' }}>
						<Grid item sx={{ marginRight: 3, maxWidth: 180 }}>
							<CerebroText variant="small" weight="medium">
								Use the checkboxes to update your progress
							</CerebroText>
						</Grid>
						<Grid item sx={{ marginRight: 3, textAlign: 'center' }}>
							<DirectConnectMilestoneStatusItem
								size={2}
								isFetching={isFetching}
								display="Review Documents"
								type="DocumentsDownloaded"
								response={directConnectReport?.documentsDownloaded}
								onClick={() =>
									setMarkingMilestone({
										milestoneType: 'DocumentsReviewed',
										displayName: 'Documents Reviewed',
									})
								}
							/>
						</Grid>
						<Grid item sx={{ marginRight: 3, textAlign: 'center' }}>
							<DirectConnectMilestoneStatusItem
								size={2}
								isFetching={isFetching}
								display="Term Sheet"
								type="TermSheetSubmitted"
								response={directConnectReport?.termSheetSubmitted}
								onClick={() =>
									setMarkingMilestone({
										milestoneType: 'TermSheetSubmitted',
										displayName: 'Term Sheet Submitted',
									})
								}
							/>
						</Grid>
						<Grid item sx={{ marginRight: 3, textAlign: 'center' }}>
							<DirectConnectMilestoneStatusItem
								size={2}
								isFetching={isFetching}
								display="Underwriting"
								type="Underwriting"
								response={directConnectReport?.underwriting}
								onClick={() =>
									setMarkingMilestone({ milestoneType: 'Underwriting', displayName: 'Underwriting' })
								}
							/>
						</Grid>
						<Grid item sx={{ marginRight: 3, textAlign: 'center' }}>
							<DirectConnectMilestoneStatusItem
								size={2}
								isFetching={isFetching}
								display="Funded"
								type="Funded"
								response={directConnectReport?.funded}
								onClick={() => setMarkingMilestone({ milestoneType: 'Funded', displayName: 'Funded' })}
							/>
						</Grid>
					</Grid>

					<Grid item sx={{ display: 'flex', alignItems: 'center' }}>
						<CerebroTextButton onClick={() => lenderOptOut(activeUser)}>Opt-Out</CerebroTextButton>
						<CerebroTextButton onClick={() => setContactBorrowerOpen(true)}>
							Contact Borrower
						</CerebroTextButton>
					</Grid>
				</Grid>

				<DirectConnectMilestoneMarkCompleteDialog
					dataRoomGuid={guid}
					bankContactGuid={activeUser?.bankContactGuid}
					request={markingMilestone}
					close={() => setMarkingMilestone(null)}
				/>
				<ContactBorrowerForm
					dataRoomGuid={guid}
					bankContactGuid={activeUser?.bankContactGuid}
					open={contactBorrowerOpen}
					close={() => setContactBorrowerOpen(false)}
				/>
			</div>
		</Slide>
	);
};

function useStatusData() {
	const { guid } = useParams<'guid'>();
	const { currentUser } = useAuth();

	const dataRoomQuery = useDataRoomQuery(guid);
	const dealQuery = useDealQuery(dataRoomQuery.data?.dealGuid ?? '', {
		enabled: dataRoomQuery.isSuccess,
		retry: false,
	});
	const integrationsQuery = useIntegrationConfigQuery();
	const summaryQuery = useDataRoomSummaryQuery(guid);
	const confirmationQuery = useDataRoomConfirmationQuery(guid, {
		enabled: currentUser.isEmployeeUser || currentUser.isCompanyUser,
		retry: false,
	});

	const summaryData = summaryQuery.data;
	const requestedAmount = dealQuery.data?.totalRequestedAmount ?? 0;
	const confirmation = confirmationQuery.data;
	const dealGuid = dealQuery.data?.guid;
	const hubspotId = dealQuery.data?.hubSpotId;
	const companyGuid = dealQuery.data?.companyGuid;
	const hubspotPortalId = integrationsQuery.data?.hubspotPortalId;
	const isRawLead = dataRoomQuery.data?.isRawLead ?? false;

	return {
		isSuccess:
			(dataRoomQuery.isSuccess && dealQuery.isSuccess && summaryQuery.isSuccess && integrationsQuery.isSuccess) ||
			confirmationQuery.isSuccess,
		isFetching:
			dataRoomQuery.isFetching ||
			dealQuery.isFetching ||
			summaryQuery.isFetching ||
			confirmationQuery.isFetching ||
			integrationsQuery.isFetching,
		summaryData,
		requestedAmount,
		confirmation,
		dealGuid,
		hubspotId,
		companyGuid,
		hubspotPortalId,
		isRawLead,
	};
}

function useLenderStatusData() {
	const { guid } = useParams<'guid'>();
	const { currentUser } = useAuth();

	const statusBarQuery = useDataRoomLenderStatusBarQuery(guid, {
		enabled: currentUser.isBankUser || currentUser.isTeamMemberUser,
		retry: false,
	});

	return {
		isSuccess: statusBarQuery.isSuccess,
		isFetching: statusBarQuery.isFetching,
		report: statusBarQuery.data?.report,
		activeUser: statusBarQuery.data?.roomUser,
		directConnectReport: statusBarQuery.data?.directConnectReport,
	};
}

export interface StatusBarProps {
	currentUser: CurrentUser;
	isDirectIntroDeal: boolean;
	viewMilestoneDetail: (report: MilestoneReport | null | undefined) => void;
	lenderCallRequest: (activeUser: ActiveDataRoomUser | null | undefined) => void;
	lenderOptOut: (user: ActiveDataRoomUser | null | undefined) => void;
	launchDeal: (confirmation: DataRoomConfirmation | null | undefined) => void;
}

const BuildEmailUrl = () =>
	`mailto:sales@cerebrocapital.com?subject=Help: Cerebro Data Room&body=Hi, I have some questions and need help with my data room here: ${encodeURI(
		window.location.href
	)}`;
